'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { Auth, Hub } from 'aws-amplify';
import { HubPayload } from '@aws-amplify/core';
import { v4 as uuidv4 } from 'uuid';
import Cookies from 'js-cookie';

import '@/core/amplify';

import { getLocalStorage, setLocalStorage } from '@/utils/localStorage';

import { USER_SUBSCRIPTIONS_KEY } from './SubscriptionContext';
import { RETURN_URL_KEY } from '@/components/common/Layout/Layout';
import { useLocalStorage } from 'usehooks-ts';

export interface StartOptions {
  handle: string;
  metaFields?: Record<string, string>;
  sensitiveMetaFields?: Record<string, string>;
  adaReadyCallback?: () => void;
}

type FlatObject = {
  [key: string]: string | number | boolean | null;
};

declare global {
  interface Window {
    adaEmbed?: {
      deleteHistory: () => Promise<void>;
      setMetaFields: (metaFields: FlatObject) => void;
      setSensitiveMetaFields: (sensitiveMetaFields: FlatObject) => void;
      start: (adSettings: StartOptions) => Promise<void>;
      triggerAnswer(answerId: string): void;
      toggle(): Promise<void>;
    };
  }
}

enum AuthEvents {
  SIGN_IN = 'signIn',
  SIGN_OUT = 'signOut',
}

export interface AuthState {
  // Attributes are defined in Cognito.
  attributes: {
    'custom:account_manager_id': string;
    loveveryId: string;
    email: string;
    email_verified: boolean;
    family_name: string;
    name: string;
    sub: string; // Cognito-specific unique ID.
  };
}

export interface AuthContextValue {
  user: AuthState | null;
  authLoading: boolean;
  signUp: (email: string, password: string) => Promise<boolean>;
  signIn: (email: string, password: string) => Promise<boolean>;
  signOut: () => Promise<{ error: null | unknown }>;
  getAndCheckCognitoUser: () => Promise<{ error: any | unknown }>;
}

const getAuthStateFromLocalStorage = () => getLocalStorage('user');

// This function extracts only the relevant auth data from events.
const transformAuthEventData = (data: any): AuthState | null => {
  if (!data) {
    return null;
  }

  return {
    attributes: {
      ...data.attributes,
      loveveryId: data.attributes['custom:account_manager_id'],
    },
  };
};

const AuthContext = createContext({} as AuthContextValue);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: any) => {
  // Auth Context State
  const [authState, setAuthState] = useState<AuthState | null>(
    getAuthStateFromLocalStorage,
  );

  const [authLoading, setAuthLoading] = useState(true);
  const [_userJwt, setUserJwt] = useLocalStorage<string | null>(
    'le-user-jwt',
    null,
  );

  const setAuthStateAndStorage = (newAuthState: AuthState | null) => {
    setLocalStorage('user', newAuthState);
    setAuthState(newAuthState);
  };

  const clearCookies = () => {
    const domain = process.env.NEXT_PUBLIC_VANITY_DOMAIN as string;
    Cookies.remove(USER_SUBSCRIPTIONS_KEY, {
      path: '/',
      domain,
    });
    Cookies.remove(RETURN_URL_KEY, {
      path: '/',
      domain,
    });
  };

  // Auth Context Events
  const handleSignIn = useCallback((data: any) => {
    const user = transformAuthEventData(data);
    setAuthStateAndStorage(user);
  }, []);

  const handleSignOut = useCallback(() => {
    setAuthStateAndStorage(null);
    clearCookies();
    window.adaEmbed?.deleteHistory();
    localStorage.removeItem('ada-embed_is-drawer-open');
  }, []);

  const handleAuthEvent = useCallback(
    (payload: HubPayload) => {
      const { event, data } = payload;
      switch (event) {
        default: {
          return;
        }

        case AuthEvents.SIGN_IN: {
          handleSignIn(data);
          return;
        }
        case AuthEvents.SIGN_OUT: {
          handleSignOut();
          return;
        }
      }
    },
    [handleSignIn, handleSignOut],
  );

  const getAndCheckCognitoUser = useCallback(async () => {
    try {
      let cognitoUser = await Auth.currentAuthenticatedUser();
      if (
        !(
          'custom:account_manager_id' in
          cognitoUser?.signInUserSession?.idToken?.payload
        )
      ) {
        await cognitoUser.refreshSession(
          cognitoUser.signInUserSession.refreshToken,
        );
        cognitoUser = await Auth.currentAuthenticatedUser({
          bypassCache: true,
        });
      }
      if (
        cognitoUser &&
        cognitoUser.signInUserSession &&
        cognitoUser.signInUserSession.idToken
      )
        return cognitoUser;
    } catch (error) {
      return null;
    }
  }, []);

  const getUser = useCallback(async () => {
    try {
      const data = await getAndCheckCognitoUser();
      const user = transformAuthEventData(data);
      setAuthStateAndStorage(user);
      setAuthLoading(false);
      return { error: null };
    } catch (error) {
      setAuthStateAndStorage(null);
      setAuthLoading(false);
      return { error };
    }
  }, []);

  const signUp = async (email: string, password: string) => {
    const username = uuidv4();
    const storeDomain = process.env.NEXT_PUBLIC_VANITY_DOMAIN as string;

    // bypass email verification for checkout sign-ups
    const clientMetadata = {
      auto_confirm: 'true',
      store_domain: storeDomain,
    };

    try {
      await Auth.signUp({
        username,
        password,
        attributes: {
          email,
        },
        clientMetadata,
      });

      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      await Auth.signIn(email, password);
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  };

  const signOut = async () => {
    try {
      await Auth.signOut();
      setAuthStateAndStorage(null);
      return { error: null };
    } catch (error) {
      console.error(error);
      return { error };
    }
  };

  interface CognitoSession {
    idToken: {
      jwtToken: string;
    };
  }

  const getUserJwt = async () => {
    try {
      const cognitoSession = await Auth.currentSession();
      if (!cognitoSession) return null;

      const {
        idToken: { jwtToken },
      } = cognitoSession as unknown as CognitoSession;

      // save jwt to cookie;
      fetch('/api/save-user-jwt', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userJwt: jwtToken,
        }),
      });

      return (cognitoSession as unknown as CognitoSession).idToken.jwtToken;
    } catch (e) {
      return null;
    }
  };

  useEffect(() => {
    const authEventListener = () => {
      Hub.listen('auth', (data) => handleAuthEvent(data.payload));
    };

    authEventListener();
    getUser();
  }, [handleAuthEvent, getUser]);

  useEffect(() => {
    const retrieveUserJwt = async () => {
      const userJwt = await getUserJwt();
      if (userJwt) {
        setUserJwt(userJwt);
      }
    };

    retrieveUserJwt();
  }, []);

  // Auth Context Value
  const authContextValue = {
    user: authState,
    authLoading,
    signUp,
    signIn,
    signOut,
    getAndCheckCognitoUser,
  };

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export default useAuth;
